import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { GenericTable } from "../../../Shared/components/Table/GenericTable";
import { TableActionButtons } from "../../../Shared/components/Table/TableActionButtons";
import { PageOptionsDto } from "../../../Shared/dto/page.options.dto";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { CompanyService } from "../services/company.service";
import { ICompany } from "../models/Company";
import { CompanyForm } from "./CompanyForm";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { ConfirmDialog } from "../../../Shared/components/ConfirmDialog";
import { AuthService } from "../../Auth/services/AuthService";
import { FileImportForm } from "../../../Shared/components/FileImportForm";
import {
  ImportFileActionType,
  ImportFileLabelType,
} from "../../../Shared/enums/importFileActionType";

export const Companies = () => {
  const {t} = useTranslation()
  const navigate = useNavigate();
  const message = t('loading_message');
  const isAllowed = AuthService.isManagementAllowed();

  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState<ICompany[]>([]);

  const [selectedCompany, setSelectedCompany] = useState<ICompany>({});

  const findCompanies = async () => {
    try {
      const pageOptions: PageOptionsDto = {
        page: pagination.pageIndex + 1,
        take: pagination.pageSize,
        query: globalFilter,
      };
      setIsLoading(true);
      const response = await CompanyService.getAll(pageOptions);
      setIsLoading(false);
      return response;
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const query = useQuery({
    queryKey: [
      "company",
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
    ],
    queryFn: findCompanies,
    placeholderData: (previous) => previous?.data,
  });

  const debouncedSetFilter = useMemo(
    () =>
      debounce((val: string) => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        setGlobalFilter(val);
      }, 200),
    []
  );

  const onPaginationChange = (pageIndex: number, pageSize: number) => {
    setPagination({ pageIndex, pageSize });
  };

  const getPageCount = () => {
    const total = query.data?.meta?.itemCount ?? 0;
    return Math.ceil(total / pagination.pageSize);
  };

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const onImportAction = () => setIsImportModalOpen(!isModalOpen);
  const toggleImportModal = (success: boolean) => {
    setIsImportModalOpen(!isImportModalOpen);
    if (success) {
      query.refetch();
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const onAddAction = () => setIsModalOpen(!isModalOpen);
  const toggleModal = (success: boolean) => {
    setIsModalOpen(!isModalOpen);
    if (success) {
      query.refetch();
    }
  };

  const onRowEditAction = (row: ICompany) => {
    setSelectedCompany(row);
    onAddAction();
  };

  const onRowViewAction = (row: ICompany) => {
    setSelectedCompany(row);
    navigate(`/services/accounts/companies/details/?company=${row.id}`);
  };

  const onRowDeleteAction = (row: ICompany) => {
    setSelectedCompany(row);
    setIsDialogOpen(true);
  };

  const deleteAction = async () => {
    const approvals =
      selectedCompanies?.length > 0
        ? selectedCompanies.map((x) => x.id!)
        : [selectedCompany?.id ?? ""];
    try {
      const response = await CompanyService.delete(approvals);
      if (response) {
        query.refetch();
      }
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    }
  };

  const onExportAction = () => {
    UtilService.exportToExcel(query?.data?.data, "Company");
  };

  const columns = useMemo<ColumnDef<ICompany>[]>(
    () => [
      {
        accessorKey: "name",
        header: t('company_name_label'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "country",
        header: t('country_label'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "address",
        header: t('address'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "isApproved",
        header: t('approved'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "createdAt",
        header: t('date_created'),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    []
  );

  const tableAction = !isAllowed ? (
    <TableActionButtons
      onAdd={onAddAction}
      onImport={onImportAction}
      onExport={onExportAction}
    />
  ) : (
    <TableActionButtons onExport={onExportAction} />
  );

  return (
    <div className="">
      <div className="relative">
        {isModalOpen && (
          <CompanyForm onClose={toggleModal} company={selectedCompany} />
        )}
        {isImportModalOpen && (
          <FileImportForm
            onClose={toggleImportModal}
            actionType={ImportFileActionType.COMPANIES}
            actionLabel={ImportFileLabelType.COMPANIES}
          />
        )}

        <GenericTable
          isLoading={isLoading}
          data={query?.data?.data ?? []}
          meta={query?.data?.meta ?? 0}
          columns={columns}
          globalFilter={globalFilter}
          onGlobalFilterChange={debouncedSetFilter}
          pageCount={getPageCount()}
          onPaginationChange={onPaginationChange}
          onRowSelectionChange={setSelectedCompanies}
          {...(!isAllowed && { onEdit: onRowEditAction })}
          onMore={onRowViewAction}
          onDelete={onRowDeleteAction}
          actionButtons={tableAction}
        />
      </div>
      <ConfirmDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={deleteAction}
        itemName={selectedCompany.name}
      />
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </div>
  );
};
